import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const LOGO_URL = "/summit-logo.png";

const heroBottle = "/no1-ivory-bottle.jpg";
const mountainBg =
  "https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?auto=format&fit=crop&w=1600&q=80";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, delay: 0.15 * i, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const Cover = () => {
  return (
    <div data-testid="cover-page" className="bg-[var(--bg)]">
      {/* Hero — pure black, logo reveal */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden grain-overlay"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage: `radial-gradient(ellipse at center, rgba(212,175,55,0.12) 0%, transparent 60%)`,
          }}
        />

        <div className="relative z-10 flex flex-col items-center text-center px-6">
          <motion.div initial="hidden" animate="show" variants={fade} custom={0}>
            <img
              src={LOGO_URL}
              alt="Summit Scents — Est. 2026"
              className="w-[320px] sm:w-[420px] md:w-[520px] h-auto select-none"
              data-testid="cover-hero-logo"
              draggable={false}
            />
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fade}
            custom={2}
            className="mt-8 max-w-xl"
          >
            <p
              className="font-serif italic text-2xl md:text-3xl text-[var(--primary-text)]/85 leading-relaxed"
              style={{ fontWeight: 300 }}
            >
              "Discover. Lead. Ascend."
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fade}
            custom={3}
            className="mt-14 flex flex-col sm:flex-row items-center gap-4"
          >
            <Link to="/shop" data-testid="hero-shop-btn" className="btn-secondary">
              Enter the Shop
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Editorial intro */}
      <section className="py-32 px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.0, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <span className="text-[10px] uppercase tracking-label gold-text">
              Summit Scents
            </span>
            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Crafted with purpose.
              <br />
              <br />
              Worn with purpose.
            </h2>
            <div className="divider-gold mt-8 mb-8" />
            <p className="text-base text-[var(--secondary-text)] leading-loose max-w-md">
              Summit Scents is set apart by the sheer quality of our materials, as well as the hands that craft our products. Discover the Summit collection and uncover the feeling of confidence within every single spray.
        
            </p>
            <Link
              to="/about"
              data-testid="cover-about-link"
              className="mt-10 inline-flex items-center gap-3 text-[11px] uppercase tracking-label gold-text hover:text-[var(--primary-text)] transition"
            >
              The Summit Story <ArrowRight size={14} />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.4, ease: [0.25, 0.1, 0.25, 1] }}
            className="relative aspect-[4/5] bg-black overflow-hidden border hairline"
          >
            <img
              src={mountainBg}
              alt="Moody snow mountains"
              className="w-full h-full object-cover opacity-60"
              style={{ filter: "brightness(0.75) contrast(1.05)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-[var(--bg)]/70 to-[var(--bg)]/40" />
            <div className="absolute bottom-8 left-8 right-8">
              <img src="/mountain-mark.png" alt="" aria-hidden className="h-7 w-auto mb-3 select-none" draggable={false} />
              <p className="font-serif text-2xl"></p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Meet the Owner */}
      <section className="relative py-32 px-6 md:px-12 border-t hairline">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-2 gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1.2 }}
            className="relative aspect-[3/4] bg-black overflow-hidden border hairline grain-overlay"
          >
            <img
              src="/founder-cohen.jpg"
              alt="Cohen Stull — Founder of Summit Scents"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="founder-image"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1.2, delay: 0.2 }}
          >
            <span className="text-[10px] uppercase tracking-label gold-text">
              Meet the Owner
            </span>
            <h2 className="mt-6 font-serif text-5xl md:text-6xl leading-[1.0]">
              Cohen
              <br />
              Stull.
            </h2>
            <div className="divider-gold mt-8 mb-8" />
            <p className="text-[var(--secondary-text)] leading-loose max-w-xl">
              Hello, my name is Cohen Stull, and I am the founder of Summit Scents. I founded Summit Scents in April of 2026, due to a growing demand that I recognized in the fragrance industry — a need for luxurious fragrances that come at a reasonable price. Throughout the course of my life, I have bought countless overpriced fragrances, which led me to an idea…create a brand that gives individuals outstanding fragrances at an affordable price. It does not sound complicated, but if you are a regular fragrance consumer, you may be able to understand how challenging this is. Over the course of the last year, I have spent countless hours building a brand that upholds standards of reliability, quality, and customer satisfaction. I believe these principles are essential not only to the success of Summit Scents, but also to establishing lasting trust with the individuals who choose our brand. At Summit Scents, my goal is simple: to make the luxury fragrance experience more accessible without compromising the quality, authenticity, or attention to detail that make it special.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Cover;
